import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Save, Loader2, UserPlus, ArrowLeft, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { BILL_DENOMS, COIN_DENOMS, ALL_DENOMS, dollarsToCents, formatCents, sumDenomsCents, sumQtyCents, parseQty } from "@/lib/money";
import { computeStatus, StatusBanner } from "@/components/StatusDisplay";
import { useT, statusLabel } from "@/lib/i18n";

const emptyValues = () => ALL_DENOMS.reduce((acc, d) => ({ ...acc, [d.key]: "" }), {});

const SectionLabel = ({ children }) => (
  <p className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">{children}</p>
);

const COIN_KEYS = ["penny", "nickel", "dime", "quarter"];

const ValueRow = ({ denom, value, onChange, testid }) => {
  const t = useT();
  const label = COIN_KEYS.includes(denom.key) ? t(denom.key) : denom.label;
  return (
  <div className="flex items-center gap-3">
    <div className="flex min-h-11 w-24 shrink-0 items-center justify-center rounded-lg bg-secondary px-1.5 py-1 text-center font-display text-sm font-bold leading-tight text-foreground">
      {label}
    </div>
    <div className="relative flex-1">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 font-display text-base font-bold text-muted-foreground">$</span>
      <Input
        type="number"
        inputMode="decimal"
        min="0"
        step="0.01"
        placeholder="0.00"
        value={value}
        onChange={(e) => onChange(denom.key, e.target.value)}
        data-testid={`${testid}-${denom.key}`}
        className="h-11 pl-7 text-right text-lg tabular-nums"
      />
    </div>
  </div>
  );
};

const Row = ({ label, value, strong }) => (
  <div className="flex items-center justify-between py-1">
    <span className={strong ? "text-sm font-bold text-foreground" : "text-sm text-muted-foreground"}>{label}</span>
    <span className={`tabular-nums ${strong ? "font-display text-base font-black text-foreground" : "text-sm font-semibold text-foreground"}`}>{value}</span>
  </div>
);

const ClosingRow = ({ denom, value, onChange, testid }) => {
  const t = useT();
  const cents = parseQty(value) * denom.cents;
  const label = COIN_KEYS.includes(denom.key) ? t(denom.key) : denom.label;
  return (
    <div className="flex items-center gap-3">
      <div className="flex min-h-11 w-24 shrink-0 items-center justify-center rounded-lg bg-secondary px-1.5 py-1 text-center font-display text-sm font-bold leading-tight text-foreground">{label}</div>
      <Input
        type="number"
        inputMode="numeric"
        min="0"
        placeholder="0"
        value={value}
        onChange={(e) => onChange(denom.key, e.target.value)}
        data-testid={`${testid}-${denom.key}`}
        className="h-11 flex-1 text-center text-lg tabular-nums"
      />
      <div className="w-20 shrink-0 text-right font-display text-sm font-bold tabular-nums text-foreground">{formatCents(cents)}</div>
    </div>
  );
};

export default function NewCount() {
  const [employees, setEmployees] = useState([]);
  const [employeeName, setEmployeeName] = useState("");
  const [step, setStep] = useState(1);

  const [startingValues, setStartingValues] = useState(emptyValues());
  const [expectedCash, setExpectedCash] = useState("");
  const [adjustments, setAdjustments] = useState([]);
  const [closingValues, setClosingValues] = useState(emptyValues());
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);

  const [addEmpOpen, setAddEmpOpen] = useState(false);
  const [newEmpName, setNewEmpName] = useState("");

  const loadEmployees = () => api.listEmployees().then(setEmployees).catch(() => {});
  const t = useT();
  useEffect(() => { loadEmployees(); }, []);

  // ---- Screen 1 calcs (cents) ----
  const startingBills = sumDenomsCents(startingValues, BILL_DENOMS);
  const startingCoins = sumDenomsCents(startingValues, COIN_DENOMS);
  const startingAmount = startingBills + startingCoins;

  // ---- Screen 2 calcs (cents) ----
  const expectedCents = dollarsToCents(expectedCash);
  const totalAdjustments = adjustments.reduce((s, a) => s + dollarsToCents(a.amount), 0);
  const expectedDrawer = startingAmount + expectedCents - totalAdjustments;
  const totalBills = sumQtyCents(closingValues, BILL_DENOMS);
  const totalCoins = sumQtyCents(closingValues, COIN_DENOMS);
  const totalCash = totalBills + totalCoins;
  const overShort = totalCash - expectedDrawer;
  const status = computeStatus(overShort);
  const depositAmount = totalBills - startingBills;

  const updateStarting = (k, v) => setStartingValues((s) => ({ ...s, [k]: v }));
  const updateClosing = (k, v) => setClosingValues((s) => ({ ...s, [k]: v }));

  const addAdjustment = () => setAdjustments((a) => [...a, { description: "", amount: "" }]);
  const updateAdjustment = (i, f, v) => setAdjustments((a) => a.map((it, idx) => (idx === i ? { ...it, [f]: v } : it)));
  const removeAdjustment = (i) => setAdjustments((a) => a.filter((_, idx) => idx !== i));

  const handleAddEmployee = async () => {
    const name = newEmpName.trim();
    if (!name) return;
    try {
      const emp = await api.createEmployee(name);
      await loadEmployees();
      setEmployeeName(emp.name);
      setNewEmpName("");
      setAddEmpOpen(false);
      toast.success(`Added ${emp.name}`);
    } catch { toast.error("Could not add employee"); }
  };

  const resetForm = () => {
    setEmployeeName("");
    setStartingValues(emptyValues());
    setExpectedCash("");
    setAdjustments([]);
    setClosingValues(emptyValues());
    setNotes("");
    setStep(1);
  };

  const clearClosing = () => {
    setExpectedCash("");
    setAdjustments([]);
    setClosingValues(emptyValues());
    setNotes("");
    setClearOpen(false);
    window.scrollTo(0, 0);
  };

  const goToClosing = () => {
    if (!employeeName) return toast.error("Select an employee first");
    if (startingAmount <= 0) return toast.error("Enter the starting drawer amount");
    setStep(2);
    window.scrollTo(0, 0);
  };

  const handleSave = async () => {
    if (totalCash <= 0) return toast.error("Count the closing drawer before saving");
    setSaving(true);
    const denomPayload = (values) => ALL_DENOMS.reduce((acc, d) => ({ ...acc, [d.key]: dollarsToCents(values[d.key]) / 100 }), {});
    const payload = {
      employee_name: employeeName,
      starting_denoms: denomPayload(startingValues),
      expected_cash: expectedCents / 100,
      adjustments: adjustments
        .filter((a) => a.description.trim() || dollarsToCents(a.amount) > 0)
        .map((a) => ({ description: a.description.trim() || "Payout", amount: dollarsToCents(a.amount) / 100 })),
      closing_denoms: ALL_DENOMS.reduce((acc, d) => ({ ...acc, [d.key]: (parseQty(closingValues[d.key]) * d.cents) / 100 }), {}),
      notes: notes.trim(),
    };
    try {
      await api.createCount(payload);
      toast.success("Count saved");
      resetForm();
    } catch { toast.error("Could not save count"); }
    finally { setSaving(false); }
  };

  // ============ SCREEN 1 ============
  if (step === 1) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-foreground">{t("starting_amount")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("starting_sub")}</p>
        </div>

        <Card className="rounded-2xl border-border p-6">
          <SectionLabel>{t("employee")}</SectionLabel>
          <div className="flex gap-3">
            <Select value={employeeName} onValueChange={setEmployeeName}>
              <SelectTrigger data-testid="employee-select" className="h-12 flex-1 text-base">
                <SelectValue placeholder={t("select_employee")} />
              </SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.name} data-testid={`employee-option-${e.id}`}>{e.name}</SelectItem>
                ))}
                {employees.length === 0 && <div className="px-2 py-3 text-sm text-muted-foreground">{t("no_employees")}</div>}
              </SelectContent>
            </Select>
            <Button type="button" variant="outline" className="h-12 rounded-xl" onClick={() => setAddEmpOpen(true)} data-testid="quick-add-employee-btn">
              <UserPlus className="h-5 w-5" />
            </Button>
          </div>
        </Card>

        <Card className="rounded-2xl border-border p-6">
          <SectionLabel>{t("starting_drawer")}</SectionLabel>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest text-primary">{t("bills")}</p>
              {BILL_DENOMS.map((d) => <ValueRow key={d.key} denom={d} value={startingValues[d.key]} onChange={updateStarting} testid="start-denom" />)}
            </div>
            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest text-primary">{t("coins")}</p>
              {COIN_DENOMS.map((d) => <ValueRow key={d.key} denom={d} value={startingValues[d.key]} onChange={updateStarting} testid="start-denom" />)}
            </div>
          </div>
          <div className="mt-5 space-y-1.5 rounded-xl bg-secondary p-4 text-sm">
            <Row label={t("starting_bills")} value={formatCents(startingBills)} />
            <Row label={t("starting_coins")} value={formatCents(startingCoins)} />
            <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
              <span className="text-xs font-bold uppercase tracking-widest text-foreground">{t("starting_amount")}</span>
              <span className="font-display text-2xl font-black tabular-nums text-foreground" data-testid="starting-amount-value">{formatCents(startingAmount)}</span>
            </div>
          </div>
        </Card>

        <Button type="button" onClick={goToClosing} data-testid="count-closing-btn" className="h-14 w-full rounded-xl text-lg font-bold active:scale-[0.98]">
          {t("count_closing_drawer")} <ArrowRight className="ml-2 h-5 w-5" />
        </Button>

        <Dialog open={addEmpOpen} onOpenChange={setAddEmpOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("add_employee")}</DialogTitle></DialogHeader>
            <Input placeholder={t("employee_name_ph")} value={newEmpName} onChange={(e) => setNewEmpName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleAddEmployee()} data-testid="new-employee-name-input" className="h-12" autoFocus />
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddEmpOpen(false)}>{t("cancel")}</Button>
              <Button onClick={handleAddEmployee} data-testid="confirm-add-employee-btn">{t("add")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ============ SCREEN 2 ============
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => setStep(1)} data-testid="back-to-starting-btn" className="-ml-2 h-10 text-muted-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" /> {t("starting_amount")}
        </Button>
        <button
          type="button"
          onClick={() => setClearOpen(true)}
          data-testid="clear-closing-btn"
          className="mr-2 text-sm font-semibold text-muted-foreground hover:text-rose-600"
        >
          {t("clear")}
        </button>
      </div>

      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
          {t("counting_for")} {employeeName}
        </h1>
      </div>

      {/* Expected cash + payouts */}
      <Card className="rounded-2xl border-border p-6">
        <SectionLabel>{t("expected_cash_ticket")}</SectionLabel>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-display text-lg font-bold text-muted-foreground">$</span>
          <Input type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.00" value={expectedCash} onChange={(e) => setExpectedCash(e.target.value)} data-testid="expected-cash-input" className="h-12 pl-7 text-lg tabular-nums" />
        </div>

        <div className="mt-5 mb-3 flex items-center justify-between">
          <SectionLabel>{t("payouts_removed")}</SectionLabel>
          <span className="text-xs font-semibold text-muted-foreground">{t("subtracted_note")}</span>
        </div>
        <div className="space-y-3">
          {adjustments.map((a, idx) => (
            <div key={idx} className="flex gap-2">
              <Input placeholder={t("adjustment_desc_ph")} value={a.description} onChange={(e) => updateAdjustment(idx, "description", e.target.value)} data-testid={`adjustment-desc-${idx}`} className="h-11 flex-1" />
              <div className="relative w-28 shrink-0">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">$</span>
                <Input type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.00" value={a.amount} onChange={(e) => updateAdjustment(idx, "amount", e.target.value)} data-testid={`adjustment-amount-${idx}`} className="h-11 pl-6 tabular-nums" />
              </div>
              <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0 text-muted-foreground hover:text-rose-600" onClick={() => removeAdjustment(idx)} data-testid={`remove-adjustment-${idx}`}>
                <X className="h-5 w-5" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" className="h-11 w-full rounded-xl border-dashed" onClick={addAdjustment} data-testid="add-adjustment-btn">
            <Plus className="mr-2 h-4 w-4" /> {t("add_payout")}
          </Button>
        </div>

        <div className="mt-5 space-y-1.5 rounded-xl bg-secondary p-4 text-sm">
          <Row label={t("starting_amount")} value={formatCents(startingAmount)} />
          <Row label={t("expected_cash")} value={formatCents(expectedCents)} />
          <Row label={t("payouts")} value={`−${formatCents(totalAdjustments)}`} />
          <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
            <span className="text-xs font-bold uppercase tracking-widest text-foreground">{t("expected_drawer")}</span>
            <span className="font-display text-2xl font-black tabular-nums text-foreground" data-testid="expected-drawer-value">{formatCents(expectedDrawer)}</span>
          </div>
        </div>
      </Card>

      {/* Closing count */}
      <Card className="rounded-2xl border-border p-6">
        <SectionLabel>{t("closing_count")}</SectionLabel>
        <p className="-mt-2 mb-3 text-xs text-muted-foreground">{t("closing_sub")}</p>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="space-y-3">
            <p className="text-xs font-bold uppercase tracking-widest text-primary">{t("bills")}</p>
            {BILL_DENOMS.map((d) => <ClosingRow key={d.key} denom={d} value={closingValues[d.key]} onChange={updateClosing} testid="close-denom" />)}
          </div>
          <div className="space-y-3">
            <p className="text-xs font-bold uppercase tracking-widest text-primary">{t("coins")}</p>
            {COIN_DENOMS.map((d) => <ClosingRow key={d.key} denom={d} value={closingValues[d.key]} onChange={updateClosing} testid="close-denom" />)}
          </div>
        </div>
        <div className="mt-5 space-y-1.5 rounded-xl bg-secondary p-4 text-sm">
          <Row label={t("total_bills_counted")} value={formatCents(totalBills)} />
          <Row label={t("total_coins_counted")} value={formatCents(totalCoins)} />
          <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
            <span className="text-xs font-bold uppercase tracking-widest text-foreground">{t("total_cash_counted")}</span>
            <span className="font-display text-2xl font-black tabular-nums text-foreground" data-testid="total-cash-value">{formatCents(totalCash)}</span>
          </div>
        </div>
      </Card>

      {/* Over/Short */}
      <StatusBanner status={status} differenceCents={overShort} testid="status-banner" />

      {/* Deposit — single amount */}
      <Card className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-6">
        <p className="text-xs font-bold uppercase tracking-widest text-primary">{t("deposit_amount")}</p>
        <p className="mt-1 font-display text-5xl font-black tabular-nums text-primary" data-testid="deposit-amount-value">{formatCents(depositAmount)}</p>
        <div className="mt-4 space-y-1.5 text-sm">
          <div className="flex justify-between text-muted-foreground"><span>{t("total_bills_counted")}</span><span className="tabular-nums">{formatCents(totalBills)}</span></div>
          <div className="flex justify-between text-muted-foreground"><span>{t("starting_bills_returned")}</span><span className="tabular-nums">−{formatCents(startingBills)}</span></div>
        </div>
      </Card>

      {/* Reconciliation */}
      <Card className="rounded-2xl border-border p-6">
        <SectionLabel>{t("drawer_reconciliation")}</SectionLabel>
        <Row label={t("starting_amount")} value={formatCents(startingAmount)} />
        <Row label={t("starting_bills")} value={formatCents(startingBills)} />
        <Row label={t("starting_coins")} value={formatCents(startingCoins)} />
        <Row label={t("expected_cash")} value={formatCents(expectedCents)} />
        <Row label={t("payouts_removed")} value={`−${formatCents(totalAdjustments)}`} />
        <div className="border-t border-border pt-1"><Row label={t("expected_drawer")} value={formatCents(expectedDrawer)} strong /></div>
        <div className="mt-1 border-t border-border pt-1">
          <Row label={t("total_bills_counted")} value={formatCents(totalBills)} />
          <Row label={t("total_coins_counted")} value={formatCents(totalCoins)} />
          <Row label={t("total_cash_counted")} value={formatCents(totalCash)} strong />
          <Row label={t("over_short")} value={status === "BALANCED" ? t("balanced") : `${statusLabel(t, status)} ${formatCents(Math.abs(overShort))}`} strong />
        </div>
        <div className="mt-4 space-y-1.5 rounded-xl bg-secondary p-4 text-sm">
          <Row label={t("total_bills_counted")} value={formatCents(totalBills)} />
          <Row label={t("starting_bills_returned")} value={`−${formatCents(startingBills)}`} />
          <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
            <span className="text-xs font-bold uppercase tracking-widest text-foreground">{t("amount_to_be_deposited")}</span>
            <span className="font-display text-2xl font-black tabular-nums text-foreground" data-testid="amount-to-deposit-value">{formatCents(depositAmount)}</span>
          </div>
        </div>
      </Card>

      {/* Notes + Save */}
      <Card className="rounded-2xl border-border p-6">
        <SectionLabel>{t("notes_optional")}</SectionLabel>
        <Textarea placeholder={t("notes_ph")} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="notes-input" className="min-h-[80px] resize-none" />
        <Button type="button" disabled={totalCash <= 0 || saving} onClick={handleSave} data-testid="save-count-btn" className="mt-4 h-14 w-full rounded-xl text-lg font-bold active:scale-[0.98]">
          {saving ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Save className="mr-2 h-5 w-5" />}
          {t("save_count")}
        </Button>
      </Card>

      <AlertDialog open={clearOpen} onOpenChange={setClearOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("clear_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("clear_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={clearClosing} data-testid="confirm-clear-btn">{t("clear")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
